import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Table, Form, Alert } from "react-bootstrap"; // Import react-bootstrap components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from "xlsx";
import Header from "../Header";
import { useHistory } from "react-router-dom"; // In case you need routing for navigation

const DisplayDocRequest = () => {
  const tableRef = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [userRemarks, setUserRemarks] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const id = localStorage.getItem('userId');
  const sessionName = localStorage.getItem('userName');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://apiaksharaconnect.aksharafinalytics.in/ViewRequest/${id}`
        );
        if (!response.ok) throw new Error("Network response was not ok");

        const result = await response.json();
        setData(result.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Export to Excel
  const exportToExcel = () => {
    if (data.length === 0) {
      alert("No data to export!");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "data");
    XLSX.writeFile(wb, "data_List.xlsx");
  };

  // Open modal for reply
  const openModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  // Handle form submit (reply with remarks and multiple files)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validation: Ensure remarks and at least one file are provided
    if (!userRemarks ) {
      alert("Please provide remarks ");
      return;
    }

    const recordId = selectedRequest.id;
    setIsSubmitting(true); // Start submission process

    const formData = new FormData();
    formData.append("user_remarks", userRemarks); // Include user remarks
    formData.append("user_id", selectedRequest.id); // Include user ID
    formData.append("username", sessionName); // Include username

    // Append selected files to the form data
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('files[]', selectedFiles[i]);
    }

    try {
      // Make the POST request to the server
      const response = await fetch(`https://apiaksharaconnect.aksharafinalytics.in/sendreply/${recordId}`, {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();

      // Check if the response is success
      if (res.status === 'success') {
        setSuccessMessage(res.message); // Set success message
        setUserRemarks(''); // Reset remarks input
        setSelectedFiles([]); // Reset file inputs
        setIsModalOpen(false); // Close modal after successful submission
        setTimeout(() => {
          window.location.reload(); 
      }, 3000); 
      } else {
        setError(res.message);
        console.error('Error message from server:', res.message);
      }
    } catch (error) {
      setError('An error occurred while uploading the files.');
      console.error('Error during fetch:', error);
    } finally {
      setIsSubmitting(false);  // Stop submission process
    }
  };

  if (loading) return <div className="text-center mt-4">Loading...</div>;

  return (
    <>
      <Header />
      <div className="container-fluid mt-4">
        <div className="card shadow">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4>Request / Remarks</h4>
            <Button variant="success" onClick={exportToExcel}>
              Export to Excel
            </Button>
          </div>
          <div className="card-body">
            {error && <Alert variant="danger">{error}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            <div className="table-responsive">
              <Table ref={tableRef} striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>S.no</th>
                    <th>Request / Remarks</th>
                    <th>Files</th>
                    <th>Remarks</th>
                    <th>Date/Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 ? (
                    data.map((data, index) => (
                      <tr key={data.id}>
                        <td>{index + 1}</td>
                        <td>{data.request_type || "N/A"}</td>
                        <td>
                          {data.file_path ? (
                            <a href={`https://apiaksharaconnect.aksharafinalytics.in/${data.file_path}`} download={data.file_path || 'N/A'} target="_blank">
                              <Button variant="warning" className="ms-2 p-1 m-1">
                                <FontAwesomeIcon icon={faDownload} /> Documents
                              </Button>
                            </a>
                          ) : (
                            <span>No file available</span>
                          )}
                        </td>
                        <td>{data.user_remarks || "N/A"}</td>
                        <td>{data.upload_at || "N/A"}</td>
                        <td>
                          <Button variant="primary" onClick={() => openModal(data)} disabled={data.user_remarks === "N/A" || data.user_remarks !== null}>
                            Reply
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Reply to Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleFormSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  value={userRemarks}
                  onChange={(e) => setUserRemarks(e.target.value)}
                  rows={3}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Upload Files</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => setSelectedFiles([...e.target.files])}
                  multiple
                />
              </Form.Group>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Reply'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default DisplayDocRequest;
